import { useEffect, useState } from 'react';
import { Lock, Save, Loader2, User as UserIcon } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const CONTACT_OPTIONS = [
  { value: '',           label: 'Not set' },
  { value: 'email',      label: 'Email' },
  { value: 'phone',      label: 'Phone' },
  { value: 'post',       label: 'Post' },
  { value: 'no_contact', label: 'Do not contact' },
];

/**
 * Editable account details (name / phone / address / contact preference)
 * plus a Change Password modal. Email is read-only — changing it is a
 * separate verification flow we haven't built yet.
 */
export default function AccountDetails({ onUpdated }) {
  const [details, setDetails] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    api.get('/api/account/details')
      .then(r => setDetails(r.data))
      .catch(() => toast.error('Could not load account details'));
  }, []);

  const set = (key) => (e) => setDetails(d => ({ ...d, [key]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/api/account/details', {
        full_name: details.full_name,
        phone: details.phone,
        company_name: details.company_name,
        address_line1: details.address_line1,
        address_line2: details.address_line2,
        city: details.city,
        address_postcode: details.address_postcode,
        contact_preference: details.contact_preference || null,
      });
      setDetails(r.data);
      toast.success('Account details saved');
      if (onUpdated) onUpdated();
    } catch (err) {
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!details) {
    return (
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5 text-slate-500 text-sm">
        Loading account details…
      </div>
    );
  }

  return (
    <>
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <UserIcon size={18} className="text-emerald-400" /> Account Details
          </h2>
          <button onClick={() => setShowPwd(true)}
            className="text-xs inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-3 py-1.5 transition-colors">
            <Lock size={12} /> Change password
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full name">
            <input type="text" value={details.full_name || ''} onChange={set('full_name')}
              className={inputCls} />
          </Field>
          <Field label="Email" hint="Email is your login. To change it, contact support.">
            <input type="email" value={details.email || ''} disabled readOnly
              className={inputCls + ' opacity-60 cursor-not-allowed'} />
          </Field>
          <Field label="Phone">
            <input type="tel" value={details.phone || ''} onChange={set('phone')}
              placeholder="07700 900123" className={inputCls} />
          </Field>
          <Field label="Company">
            <input type="text" value={details.company_name || ''} onChange={set('company_name')}
              className={inputCls} />
          </Field>
        </div>

        <h3 className="text-sm uppercase tracking-wide text-slate-500 font-semibold pt-2">
          Contact address <span className="font-normal normal-case text-slate-600 text-xs ml-2">(optional — only if you want post)</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Address line 1">
            <input type="text" value={details.address_line1 || ''} onChange={set('address_line1')}
              className={inputCls} />
          </Field>
          <Field label="Address line 2">
            <input type="text" value={details.address_line2 || ''} onChange={set('address_line2')}
              className={inputCls} />
          </Field>
          <Field label="City">
            <input type="text" value={details.city || ''} onChange={set('city')}
              className={inputCls} />
          </Field>
          <Field label="Postcode">
            <input type="text" value={details.address_postcode || ''} onChange={set('address_postcode')}
              className={inputCls + ' uppercase'} />
          </Field>
        </div>

        <Field label="Preferred contact channel"
          hint="Transactional emails (alerts, billing) are always sent regardless.">
          <select value={details.contact_preference || ''} onChange={set('contact_preference')}
            className={inputCls}>
            {CONTACT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>

        <div className="flex justify-end pt-2">
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save details
          </button>
        </div>
      </div>

      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
    </>
  );
}


function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/account/change-password', {
        current_password: current,
        new_password: next,
      });
      toast.success('Password changed');
      onClose();
    } catch (err) {
      toast.error(err?.message || err?.data?.detail || 'Could not change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Lock size={18} className="text-emerald-400" /> Change password
        </h3>

        <Field label="Current password">
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required
            autoComplete="current-password" className={inputCls} />
        </Field>
        <Field label="New password" hint="Minimum 8 characters.">
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required
            minLength={8} autoComplete="new-password" className={inputCls} />
        </Field>
        <Field label="Confirm new password">
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
            minLength={8} autoComplete="new-password" className={inputCls} />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">
            Cancel
          </button>
          <button type="submit" disabled={submitting}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm font-medium">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
            Change password
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = 'w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500';

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-slate-400 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-slate-600 mt-1">{hint}</span>}
    </label>
  );
}
